/**
 * Facturación electrónica del SRI (esquema offline, comprobantes versión 1.1.0): cálculo de totales,
 * validaciones y XML de la factura (01) y la nota de crédito (04). El XML se genera ya en forma canónica
 * (C14N): sin espacios entre etiquetas, atributos en orden y etiquetas vacías abiertas y cerradas, para que
 * la firma XAdES-BES se calcule sobre exactamente estos bytes.
 * Todos los montos van en centavos (enteros); el XML los escribe con dos decimales.
 */
import { claveAcceso, LIMITE_CONSUMIDOR_FINAL, type Regimen } from "./sri.ts";
import { cedulaValida, rucValido, correoValido } from "./identificacion.ts";

export const TARIFAS_IVA = [0, 5, 15] as const;
export type TarifaIva = (typeof TARIFAS_IVA)[number];
/** Código de porcentaje del IVA según la tabla 17 de la ficha técnica. */
export const CODIGO_PORCENTAJE_IVA: Readonly<Record<TarifaIva, string>> = { 0: "0", 5: "5", 15: "4" };

export type TipoIdentificacion = "ruc" | "cedula" | "pasaporte" | "consumidor_final";
export const CODIGO_IDENTIFICACION: Readonly<Record<TipoIdentificacion, string>> = { ruc: "04", cedula: "05", pasaporte: "06", consumidor_final: "07" };
export const CONSUMIDOR_FINAL = { identificacion: "9999999999999", razonSocial: "CONSUMIDOR FINAL" } as const;

/** Formas de pago del SRI (tabla 24) a partir de las de la caja de Donna. */
export const FORMA_PAGO_SRI: Readonly<Record<string, string>> = {
  "Efectivo": "01", "Transferencia": "20", "Tarjeta de débito": "16", "Tarjeta de crédito": "19", "Cheque": "02", "DeUna": "20",
};
export const NOMBRE_FORMA_PAGO: Readonly<Record<string, string>> = {
  "01": "Sin utilización del sistema financiero", "02": "Cheque propio", "16": "Tarjeta de débito", "19": "Tarjeta de crédito", "20": "Otros con utilización del sistema financiero",
};

export const AMBIENTES = { 1: "Pruebas", 2: "Producción" } as const;
export type Ambiente = 1 | 2;

export interface Emisor {
  ruc: string; razonSocial: string; nombreComercial: string; dirMatriz: string; dirEstablecimiento: string;
  obligadoContabilidad: boolean; regimen: Regimen; contribuyenteEspecial: string; agenteRetencion: string;
  establecimiento: string; puntoEmision: string; ambiente: Ambiente;
}
export interface Comprador { tipo: TipoIdentificacion; identificacion: string; razonSocial: string; direccion: string; correo: string; telefono: string }
export interface LineaFactura { codigo: string; descripcion: string; cantidad: number; precioUnitario: number; descuento: number; tarifaIva: TarifaIva }
export interface PagoFactura { formaPago: string; total: number }

export interface TotalesFactura {
  lineas: (LineaFactura & { base: number; iva: number })[];
  porTarifa: { tarifa: TarifaIva; base: number; iva: number }[];
  subtotal: number; descuento: number; iva: number; total: number;
}

/** Totales como los valida el SRI: cada línea redondeada a centavos; el IVA se suma por tarifa. */
export function totalesFactura(lineas: LineaFactura[]): TotalesFactura {
  const ls = lineas.map((l) => {
    const base = Math.round(l.cantidad * l.precioUnitario) - l.descuento;
    return { ...l, base, iva: Math.round((base * l.tarifaIva) / 100) };
  });
  const porTarifa = TARIFAS_IVA.map((t) => ({ tarifa: t, base: ls.filter((l) => l.tarifaIva === t).reduce((s, l) => s + l.base, 0), iva: 0 }))
    .filter((x) => ls.some((l) => l.tarifaIva === x.tarifa));
  for (const x of porTarifa) x.iva = Math.round((x.base * x.tarifa) / 100);
  const subtotal = ls.reduce((s, l) => s + l.base, 0);
  const iva = porTarifa.reduce((s, x) => s + x.iva, 0);
  return { lineas: ls, porTarifa, subtotal, descuento: ls.reduce((s, l) => s + l.descuento, 0), iva, total: subtotal + iva };
}

export function problemasEmisor(e: Emisor, tieneFirma: boolean): string[] {
  const p: string[] = [];
  if (!rucValido(e.ruc)) p.push("El RUC de la clínica no es válido (13 dígitos que terminan en 001).");
  if (e.razonSocial.trim().length < 3) p.push("Falta la razón social tal como está en el RUC.");
  if (e.dirMatriz.trim().length < 5) p.push("Falta la dirección de la matriz.");
  if (!/^\d{3}$/.test(e.establecimiento) || e.establecimiento === "000") p.push("El establecimiento debe tener 3 dígitos (por ejemplo 001).");
  if (!/^\d{3}$/.test(e.puntoEmision) || e.puntoEmision === "000") p.push("El punto de emisión debe tener 3 dígitos (por ejemplo 001).");
  if (e.contribuyenteEspecial && !/^\d{3,13}$/.test(e.contribuyenteEspecial)) p.push("El número de contribuyente especial solo lleva dígitos.");
  if (e.agenteRetencion && !/^\d{1,8}$/.test(e.agenteRetencion)) p.push("El número de resolución de agente de retención solo lleva dígitos.");
  if (!tieneFirma) p.push("Sube la firma electrónica (.p12) de la clínica.");
  return p;
}

export function problemasComprador(c: Comprador): string[] {
  const p: string[] = [];
  if (c.tipo === "consumidor_final") return c.correo && !correoValido(c.correo) ? ["El correo no es válido."] : [];
  if (c.tipo === "cedula" && !cedulaValida(c.identificacion)) p.push("La cédula del cliente no es válida.");
  if (c.tipo === "ruc" && !rucValido(c.identificacion)) p.push("El RUC del cliente no es válido.");
  if (c.tipo === "pasaporte" && !/^[A-Z0-9]{5,20}$/i.test(c.identificacion)) p.push("El pasaporte debe tener entre 5 y 20 letras o números.");
  if (c.razonSocial.trim().length < 3) p.push("Escribe el nombre o razón social del cliente.");
  if (c.razonSocial.length > 300) p.push("El nombre del cliente es demasiado largo.");
  if (c.correo && !correoValido(c.correo)) p.push("El correo del cliente no es válido.");
  if (c.direccion.length > 300) p.push("La dirección es demasiado larga.");
  return p;
}

export function problemasFactura(o: { regimen: Regimen; comprador: Comprador; lineas: LineaFactura[]; pagos: PagoFactura[] }): string[] {
  const p = problemasComprador(o.comprador);
  if (!o.lineas.length) p.push("Agrega al menos un servicio.");
  if (o.lineas.length > 100) p.push("Máximo 100 líneas por factura.");
  o.lineas.forEach((l, i) => {
    const n = `Línea ${i + 1}`;
    if (!l.descripcion.trim()) p.push(`${n}: falta la descripción.`);
    if (l.descripcion.length > 300) p.push(`${n}: la descripción es demasiado larga.`);
    if (!Number.isInteger(l.cantidad) || l.cantidad < 1 || l.cantidad > 9999) p.push(`${n}: la cantidad debe ser un número entero.`);
    if (!Number.isInteger(l.precioUnitario) || l.precioUnitario < 0) p.push(`${n}: el precio no es válido.`);
    if (!Number.isInteger(l.descuento) || l.descuento < 0 || l.descuento > l.cantidad * l.precioUnitario) p.push(`${n}: el descuento no puede superar el valor.`);
    if (!(TARIFAS_IVA as readonly number[]).includes(l.tarifaIva)) p.push(`${n}: tarifa de IVA no válida.`);
  });
  const t = totalesFactura(o.lineas);
  if (t.total <= 0) p.push("El total de la factura debe ser mayor que cero.");
  if (o.comprador.tipo === "consumidor_final" && t.total / 100 > LIMITE_CONSUMIDOR_FINAL[o.regimen])
    p.push(`A consumidor final solo se puede facturar hasta $${LIMITE_CONSUMIDOR_FINAL[o.regimen]}: pide la cédula o el RUC del cliente.`);
  const pagado = o.pagos.reduce((s, x) => s + x.total, 0);
  if (!o.pagos.length) p.push("Indica la forma de pago.");
  else if (pagado !== t.total) p.push(`Las formas de pago (${dec(pagado)}) deben sumar el total de la factura (${dec(t.total)}).`);
  if (o.pagos.some((x) => !Object.keys(NOMBRE_FORMA_PAGO).includes(x.formaPago))) p.push("Forma de pago no válida.");
  return p;
}

/* ---------- XML ---------- */

/** Escapa texto como lo hace la forma canónica (C14N). */
export const xmlTexto = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\r/g, "&#xD;");
const limpio = (s: string, max = 300) => s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, "").replace(/\s+/g, " ").trim().slice(0, max);
const dec = (centavos: number) => (centavos / 100).toFixed(2);
const el = (nombre: string, valor: string | number) => `<${nombre}>${xmlTexto(String(valor))}</${nombre}>`;
/** aaaa-mm-dd → dd/mm/aaaa */
export const fechaSri = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
export const numeroComprobante = (estab: string, pto: string, secuencial: number) => `${estab}-${pto}-${String(secuencial).padStart(9, "0")}`;

const RIMPE: Readonly<Record<Regimen, string>> = { general: "", emprendedor: "CONTRIBUYENTE RÉGIMEN RIMPE", popular: "CONTRIBUYENTE NEGOCIO POPULAR - RÉGIMEN RIMPE" };

export function infoTributaria(e: Emisor, codDoc: string, clave: string, secuencial: number): string {
  return "<infoTributaria>" + el("ambiente", e.ambiente) + el("tipoEmision", 1) + el("razonSocial", limpio(e.razonSocial)) +
    (e.nombreComercial.trim() ? el("nombreComercial", limpio(e.nombreComercial)) : "") +
    el("ruc", e.ruc) + el("claveAcceso", clave) + el("codDoc", codDoc) + el("estab", e.establecimiento) + el("ptoEmi", e.puntoEmision) +
    el("secuencial", String(secuencial).padStart(9, "0")) + el("dirMatriz", limpio(e.dirMatriz)) +
    (e.agenteRetencion ? el("agenteRetencion", e.agenteRetencion) : "") +
    (RIMPE[e.regimen] ? el("contribuyenteRimpe", RIMPE[e.regimen]) : "") + "</infoTributaria>";
}

export function datosComprador(c: Comprador) {
  return c.tipo === "consumidor_final"
    ? { tipo: CODIGO_IDENTIFICACION.consumidor_final, id: CONSUMIDOR_FINAL.identificacion, nombre: CONSUMIDOR_FINAL.razonSocial }
    : { tipo: CODIGO_IDENTIFICACION[c.tipo], id: c.identificacion.toUpperCase(), nombre: limpio(c.razonSocial) };
}

export function impuestosLinea(l: TotalesFactura["lineas"][number]) {
  return "<impuestos><impuesto>" + el("codigo", 2) + el("codigoPorcentaje", CODIGO_PORCENTAJE_IVA[l.tarifaIva]) + el("tarifa", l.tarifaIva) +
    el("baseImponible", dec(l.base)) + el("valor", dec(l.iva)) + "</impuesto></impuestos>";
}
export const totalConImpuestos = (t: TotalesFactura) => "<totalConImpuestos>" + t.porTarifa.map((x) => "<totalImpuesto>" + el("codigo", 2) +
  el("codigoPorcentaje", CODIGO_PORCENTAJE_IVA[x.tarifa]) + el("baseImponible", dec(x.base)) + el("valor", dec(x.iva)) + "</totalImpuesto>").join("") + "</totalConImpuestos>";

/**
 * RUC de la empresa dueña de Donna (el «proveedor del sistema»). Desde la Resolución NAC-DGERCGC26-00000027 del SRI (28 de julio de 2026) todo comprobante
 * electrónico debe llevarlo en la información adicional (campo «RUC Proveedor»). Va en todas las facturas y notas de crédito, también en las que una clínica
 * emite con la versión para consultorios; nadie lo escribe a mano. Si cambia la empresa que opera Donna, se cambia aquí.
 */
export const RUC_PROVEEDOR_SISTEMA = "1714872197001";
export const CAMPO_RUC_PROVEEDOR = "RUC Proveedor";

export function infoAdicional(c: Comprador, extra: [string, string][]) {
  const campos: [string, string][] = [];
  if (c.correo) campos.push(["Email", c.correo]);
  if (c.telefono) campos.push(["Teléfono", c.telefono]);
  if (c.direccion && c.tipo === "consumidor_final") campos.push(["Dirección", c.direccion]);
  campos.push(...extra);
  // el SRI admite 15 campos: el del proveedor siempre entra, al final
  const ok = [...campos.filter(([, v]) => limpio(v)).slice(0, 14), [CAMPO_RUC_PROVEEDOR, RUC_PROVEEDOR_SISTEMA] as [string, string]];
  return ok.length ? "<infoAdicional>" + ok.map(([n, v]) => `<campoAdicional nombre="${xmlTexto(n).replace(/"/g, "&quot;")}">${xmlTexto(limpio(v))}</campoAdicional>`).join("") + "</infoAdicional>" : "";
}

export interface DatosFactura {
  emisor: Emisor; fecha: string; secuencial: number; codigoNumerico: string;
  comprador: Comprador; lineas: LineaFactura[]; pagos: PagoFactura[]; adicionales?: [string, string][];
}

/** XML de la factura sin firmar (raíz con id="comprobante") y su clave de acceso. */
export function xmlFactura(d: DatosFactura): { xml: string; clave: string; totales: TotalesFactura } {
  const e = d.emisor, t = totalesFactura(d.lineas), c = datosComprador(d.comprador);
  const clave = claveAcceso({ fecha: d.fecha, tipoComprobante: "01", ruc: e.ruc, ambiente: String(e.ambiente) as "1" | "2", establecimiento: e.establecimiento, puntoEmision: e.puntoEmision, secuencial: d.secuencial, codigoNumerico: d.codigoNumerico });
  const xml = `<factura id="comprobante" version="1.1.0">` + infoTributaria(e, "01", clave, d.secuencial) +
    "<infoFactura>" + el("fechaEmision", fechaSri(d.fecha)) + el("dirEstablecimiento", limpio(e.dirEstablecimiento || e.dirMatriz)) +
    (e.contribuyenteEspecial ? el("contribuyenteEspecial", e.contribuyenteEspecial) : "") + el("obligadoContabilidad", e.obligadoContabilidad ? "SI" : "NO") +
    el("tipoIdentificacionComprador", c.tipo) + el("razonSocialComprador", c.nombre) + el("identificacionComprador", c.id) +
    (d.comprador.direccion && d.comprador.tipo !== "consumidor_final" ? el("direccionComprador", limpio(d.comprador.direccion)) : "") +
    el("totalSinImpuestos", dec(t.subtotal)) + el("totalDescuento", dec(t.descuento)) + totalConImpuestos(t) +
    el("propina", "0.00") + el("importeTotal", dec(t.total)) + el("moneda", "DOLAR") +
    "<pagos>" + d.pagos.map((p) => "<pago>" + el("formaPago", p.formaPago) + el("total", dec(p.total)) + "</pago>").join("") + "</pagos>" +
    "</infoFactura><detalles>" + t.lineas.map((l) => "<detalle>" + (l.codigo ? el("codigoPrincipal", limpio(l.codigo, 25)) : "") + el("descripcion", limpio(l.descripcion)) +
      el("cantidad", l.cantidad.toFixed(2)) + el("precioUnitario", dec(l.precioUnitario)) + el("descuento", dec(l.descuento)) +
      el("precioTotalSinImpuesto", dec(l.base)) + impuestosLinea(l) + "</detalle>").join("") + "</detalles>" +
    infoAdicional(d.comprador, d.adicionales ?? []) + "</factura>";
  return { xml, clave, totales: t };
}

export interface DatosNotaCredito extends DatosFactura { documentoModificado: { numero: string; fecha: string }; motivo: string }

/** Nota de crédito (anula o corrige una factura autorizada). */
export function xmlNotaCredito(d: DatosNotaCredito): { xml: string; clave: string; totales: TotalesFactura } {
  const e = d.emisor, t = totalesFactura(d.lineas), c = datosComprador(d.comprador);
  const clave = claveAcceso({ fecha: d.fecha, tipoComprobante: "04", ruc: e.ruc, ambiente: String(e.ambiente) as "1" | "2", establecimiento: e.establecimiento, puntoEmision: e.puntoEmision, secuencial: d.secuencial, codigoNumerico: d.codigoNumerico });
  const xml = `<notaCredito id="comprobante" version="1.1.0">` + infoTributaria(e, "04", clave, d.secuencial) +
    "<infoNotaCredito>" + el("fechaEmision", fechaSri(d.fecha)) + el("dirEstablecimiento", limpio(e.dirEstablecimiento || e.dirMatriz)) +
    el("tipoIdentificacionComprador", c.tipo) + el("razonSocialComprador", c.nombre) + el("identificacionComprador", c.id) +
    (e.contribuyenteEspecial ? el("contribuyenteEspecial", e.contribuyenteEspecial) : "") + el("obligadoContabilidad", e.obligadoContabilidad ? "SI" : "NO") +
    el("codDocModificado", "01") + el("numDocModificado", d.documentoModificado.numero) + el("fechaEmisionDocSustento", fechaSri(d.documentoModificado.fecha)) +
    el("totalSinImpuestos", dec(t.subtotal)) + el("valorModificacion", dec(t.total)) + el("moneda", "DOLAR") + totalConImpuestos(t) +
    el("motivo", limpio(d.motivo)) + "</infoNotaCredito><detalles>" +
    t.lineas.map((l) => "<detalle>" + (l.codigo ? el("codigoInterno", limpio(l.codigo, 25)) : "") + el("descripcion", limpio(l.descripcion)) +
      el("cantidad", l.cantidad.toFixed(2)) + el("precioUnitario", dec(l.precioUnitario)) + el("descuento", dec(l.descuento)) +
      el("precioTotalSinImpuesto", dec(l.base)) + impuestosLinea(l) + "</detalle>").join("") + "</detalles>" +
    infoAdicional(d.comprador, d.adicionales ?? []) + "</notaCredito>";
  return { xml, clave, totales: t };
}

/** Estados de un comprobante en Donna. */
export const ESTADOS_COMPROBANTE = {
  firmado: "Firmado, sin enviar", enviado: "Enviado al SRI", en_proceso: "En proceso en el SRI", autorizado: "Autorizado",
  devuelto: "Devuelto por el SRI", no_autorizado: "No autorizado", anulado: "Anulado con nota de crédito",
} as const;
export type EstadoComprobante = keyof typeof ESTADOS_COMPROBANTE;

/** Código de barras Code 128 (juego C con cambio a B para el dígito impar) de la clave de acceso, como anchos de barras. */
export function code128(digitos: string): number[] {
  const P = ["212222","222122","222221","121223","121322","131222","122213","122312","132212","221213","221312","231212","112232","122132","122231","113222","123122","123221","223211","221132","221231","213212","223112","312131","311222","321122","321221","312212","322112","322211","212123","212321","232121","111323","131123","131321","112313","132113","132311","211313","231113","231311","112133","112331","132131","113123","113321","133121","313121","211331","231131","213113","213311","213131","311123","311321","331121","312113","312311","332111","314111","221411","431111","111224","111422","121124","121421","141122","141221","112214","112412","122114","122411","142112","142211","241211","221114","413111","241112","134111","111242","121142","121241","114212","124112","124211","411212","421112","421211","212141","214121","412121","111143","111341","131141","114113","114311","411113","411311","113141","114131","311141","411131","211412","211214","211232","2331112"];
  if (!/^\d+$/.test(digitos)) throw new Error("La clave de acceso solo lleva dígitos");
  const codigos: number[] = [105]; // inicio C
  const pares = digitos.length - (digitos.length % 2);
  for (let i = 0; i < pares; i += 2) codigos.push(Number(digitos.slice(i, i + 2)));
  if (digitos.length % 2) { codigos.push(100); codigos.push(Number(digitos[digitos.length - 1]) + 16); } // cambio a B; en B "0" = 16
  const suma = codigos.reduce((s, c, i) => s + c * (i === 0 ? 1 : i), 0);
  codigos.push(suma % 103, 106);
  return codigos.flatMap((c) => P[c].split("").map(Number));
}

/** Piezas del XML que comparten los demás comprobantes (retención, liquidación de compra). */
export { limpio as limpiarSri, dec as decimalSri, el as etiquetaSri };
